import React, { useEffect, useMemo, useState } from 'react';
import './ObjectivesT4.css';

const API = 'https://cms.amservices.eu/api/objectives_t4.php';
const MONTHS = [{ number: 10, label: 'Octobre' }, { number: 11, label: 'Novembre' }, { number: 12, label: 'Décembre' }];
const TEAMS = [{ key: 'magasin', label: 'Le magasin' }, { key: 'giovanni', label: 'Giovanni' }, { key: 'eddy', label: 'Eddy' }];
const METRICS = [{ key: 'ca', label: 'Chiffre d’affaires' }, { key: 'marge', label: 'Marge' }];
const euro = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const percent = new Intl.NumberFormat('fr-FR', { style: 'percent', maximumFractionDigits: 1 });
const keyFor = (team, metric) => `${team}:${metric}`;
const objectiveKey = (month, team, metric) => `${month}:${keyFor(team, metric)}`;
const dailyKey = (date, team, metric) => `${date}:${keyFor(team, metric)}`;

function datesInMonth(month) {
  const count = new Date(2026, month, 0).getDate();
  return Array.from({ length: count }, (_, index) => `2026-${String(month).padStart(2, '0')}-${String(index + 1).padStart(2, '0')}`);
}

function latestFor(daily, month, team, metric) {
  return daily.filter(row => Number(row.date.slice(5, 7)) === month && row.team === team && row.metric === metric)
    .sort((a, b) => b.date.localeCompare(a.date))[0] || null;
}

function progressTone(rate) {
  if (rate === null) return 'tone-unset';
  if (rate < 0.5) return 'tone-red';
  if (rate < 0.75) return 'tone-orange';
  if (rate < 1) return 'tone-yellow';
  return 'tone-green';
}

export default function ObjectivesT4({ compact = false, canEditObjectives = false }) {
  const [view, setView] = useState('dashboard');
  const [month, setMonth] = useState(10);
  const [objectives, setObjectives] = useState([]);
  const [daily, setDaily] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const token = localStorage.getItem('am_token') || '';
        const response = await fetch(API, { headers: { Authorization: `Bearer ${token}` } });
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error(data.message || 'Chargement impossible.');
        if (active) {
          setObjectives(data.objectives);
          setDaily(data.daily);
          setLastSync(new Date());
          setError('');
        }
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    };
    refresh();
    const timer = compact ? window.setInterval(refresh, 60000) : null;
    return () => { active = false; if (timer) window.clearInterval(timer); };
  }, [compact]);

  const objectiveMap = useMemo(() => new Map(objectives.map(row => [objectiveKey(Number(row.month), row.team, row.metric), Number(row.target)])), [objectives]);
  const dailyMap = useMemo(() => new Map(daily.map(row => [dailyKey(row.date, row.team, row.metric), Number(row.amount)])), [daily]);

  async function save(type, detail, raw) {
    if (type === 'objective' && !canEditObjectives) return;
    const id = type === 'objective'
      ? objectiveKey(detail.month, detail.team, detail.metric)
      : dailyKey(detail.date, detail.team, detail.metric);
    const original = type === 'objective' ? objectiveMap.get(id) : dailyMap.get(id);
    const trimmed = String(raw).trim();
    const amount = trimmed === '' ? null : Number(trimmed.replace(',', '.'));
    if ((type === 'objective' && (amount === null || amount <= 0)) ||
        (amount !== null && (!Number.isFinite(amount) || amount < 0 || amount > 999999999.99))) {
      setError(type === 'objective' ? 'L’objectif doit être supérieur à zéro.' : 'Saisissez un montant positif ou laissez la cellule vide.');
      return;
    }
    if (amount === (original ?? null)) {
      setDrafts(current => { const next = { ...current }; delete next[id]; return next; });
      return;
    }
    setSaving(id);
    setError('');
    setNotice('');
    try {
      const token = localStorage.getItem('am_token') || '';
      const response = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ type, ...detail, amount }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Enregistrement impossible.');
      if (type === 'objective') {
        setObjectives(current => current.map(row => objectiveKey(Number(row.month), row.team, row.metric) === id ? { ...row, target: amount } : row));
      } else {
        setDaily(current => [...current.filter(row => dailyKey(row.date, row.team, row.metric) !== id), ...(amount === null ? [] : [{ ...detail, amount }])]);
      }
      setDrafts(current => { const next = { ...current }; delete next[id]; return next; });
      setNotice('Enregistré');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving('');
    }
  }

  function amountInput(type, detail) {
    const id = type === 'objective'
      ? objectiveKey(detail.month, detail.team, detail.metric)
      : dailyKey(detail.date, detail.team, detail.metric);
    const saved = type === 'objective' ? objectiveMap.get(id) : dailyMap.get(id);
    return <input
      className="objectives-amount-input"
      type="number" min={type === 'objective' ? '0.01' : '0'} step="0.01"
      aria-label={`${type === 'objective' ? 'Objectif' : 'Cumul'} ${detail.team} ${detail.metric} ${detail.date || detail.month}`}
      value={drafts[id] ?? (saved === undefined ? '' : String(saved))}
      disabled={saving === id}
      onChange={event => setDrafts(current => ({ ...current, [id]: event.target.value }))}
      onBlur={event => { if (Object.prototype.hasOwnProperty.call(drafts, id)) save(type, detail, event.target.value); }}
      onKeyDown={event => { if (event.key === 'Enter') event.currentTarget.blur(); }}
    />;
  }

  return <main className={`objectives-page ${compact ? 'objectives-compact' : ''}`}>
    <header className="objectives-header">
      <div><p className="objectives-eyebrow">AM Services · 4ᵉ trimestre 2026</p><h1>{compact ? 'Objectifs T4' : 'Objectifs commerciaux'}</h1><p>{compact ? 'Chiffre d’affaires et marge · mise à jour automatique' : 'Chiffre d’affaires et marge en euros'}</p></div>
      {compact && lastSync && <span className="objectives-sync">Actualisé à {lastSync.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>}
      {!compact && <div className="objectives-tabs" role="tablist" aria-label="Vues des objectifs">
        {[
          ['dashboard', 'Tableau de bord'], ['objectives', 'Objectifs'], ['daily', 'Suivi quotidien'],
        ].map(([id, label]) => <button key={id} role="tab" aria-selected={view === id} className={view === id ? 'active' : ''} onClick={() => setView(id)}>{label}</button>)}
      </div>}
    </header>
    {error && <div className="objectives-message error" role="alert">{error}</div>}
    {notice && !error && <div className="objectives-message" role="status">{notice}</div>}
    {loading ? <p>Chargement des objectifs…</p> : <>
      {(compact || view === 'dashboard') && <div className="objectives-month-grid">
        {MONTHS.map(item => <section className="objectives-month-card" key={item.number}>
          <h2>{item.label} 2026</h2>
          {TEAMS.map(team => <div className="objectives-team" key={team.key}>
            <h3>{team.label}</h3>
            {METRICS.map(metric => {
              const target = objectiveMap.get(objectiveKey(item.number, team.key, metric.key));
              const latest = latestFor(daily, item.number, team.key, metric.key);
              const rate = target > 0 && latest ? Number(latest.amount) / target : null;
              return <div className={`objectives-progress ${progressTone(rate)}`} key={metric.key}>
                <div className="objectives-progress-head"><span>{metric.label}</span><strong>{rate === null ? '—' : percent.format(rate)}</strong></div>
                <div className="objectives-track"><span style={{ width: `${Math.min(100, Math.max(0, (rate || 0) * 100))}%` }} /></div>
                <div className="objectives-progress-values"><span>{latest ? euro.format(Number(latest.amount)) : 'Réalisé à renseigner'}{latest && <small> · au {latest.date.slice(8, 10)}/{latest.date.slice(5, 7)}</small>}</span><span>Objectif : {target ? euro.format(target) : '—'}</span></div>
              </div>;
            })}
          </div>)}
        </section>)}
      </div>}
      {!compact && view === 'objectives' && <section className="objectives-panel">
        <h2>Objectifs mensuels</h2><p>{canEditObjectives ? 'Modifiez un montant, puis quittez la cellule pour l’enregistrer. Chaque objectif doit être supérieur à zéro.' : 'Les objectifs sont modifiables uniquement par le compte Aurélien.'}</p>
        <div className="objectives-table-scroll"><table><thead><tr><th>Mois</th><th>Équipe</th><th>Indicateur</th><th>Objectif (€)</th><th>Dernier relevé</th><th>Cumul retenu (€)</th><th>Atteinte</th></tr></thead>
          <tbody>{MONTHS.flatMap(item => TEAMS.flatMap(team => METRICS.map(metric => {
            const latest = latestFor(daily, item.number, team.key, metric.key);
            const target = objectiveMap.get(objectiveKey(item.number, team.key, metric.key));
            return <tr key={objectiveKey(item.number, team.key, metric.key)}><td>{item.label}</td><td>{team.label}</td><td>{metric.label}</td>
              <td>{canEditObjectives ? amountInput('objective', { month: item.number, team: team.key, metric: metric.key }) : (target === undefined ? '—' : euro.format(target))}</td>
              <td>{latest ? `${latest.date.slice(8, 10)}/${latest.date.slice(5, 7)}/2026` : '—'}</td>
              <td>{latest ? euro.format(Number(latest.amount)) : '—'}</td>
              <td>{target > 0 && latest ? percent.format(Number(latest.amount) / target) : '—'}</td></tr>;
          })))}</tbody></table></div>
      </section>}
      {!compact && view === 'daily' && <section className="objectives-panel">
        <div className="objectives-panel-heading"><div><h2>Suivi quotidien</h2><p>Saisissez le cumul depuis le début du mois. Une cellule vide conserve le dernier total connu ; 0 représente un total nul.</p></div>
          <label>Mois <select value={month} onChange={event => setMonth(Number(event.target.value))}>{MONTHS.map(item => <option key={item.number} value={item.number}>{item.label} 2026</option>)}</select></label></div>
        <div className="objectives-table-scroll"><table><thead><tr><th rowSpan="2">Date</th>{TEAMS.map(team => <th key={team.key} colSpan="2">{team.label}</th>)}</tr><tr>{TEAMS.flatMap(team => METRICS.map(metric => <th key={keyFor(team.key, metric.key)}>{metric.key === 'ca' ? 'CA' : 'Marge'}</th>))}</tr></thead>
          <tbody>{datesInMonth(month).map(date => <tr key={date}><th scope="row">{date.slice(8, 10)}/{date.slice(5, 7)}</th>{TEAMS.flatMap(team => METRICS.map(metric => <td key={keyFor(team.key, metric.key)}>{amountInput('daily', { date, team: team.key, metric: metric.key })}</td>))}</tr>)}</tbody></table></div>
      </section>}
    </>}
  </main>;
}
